// components/common/CreationModal.js
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Icon from 'components/icon/Icon';
import showNotification from '../../components/extras/showNotification';
import SearchAutoComplete from '../../components/customComponents/SearchAutoComplete';
import { AutoComplete, getPlaceDetails } from '../../googleApi/placeAutoComplete';
import { extractFromPlaceDetails } from './fieldsConfig/clientFields';
import './CreationModal.css';

const CreationModal = ({
  show,
  onHide,
  onClose,
  submit,
  title = "Créer un nouvel élément",
  fields = [],
  getDynamicFields = null,
  initialData = {},
  submitLabel = "Créer",
  cancelLabel = "Annuler",
  size = "lg",
  apiService = null,
  onSuccess = null,
  refreshOnSuccess = true
}) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [dynamicFields, setDynamicFields] = useState(fields);
  const [loadingFields, setLoadingFields] = useState(false);
  const [hasLoadedFields, setHasLoadedFields] = useState(false);
  const [searchResults, setSearchResults] = useState({});
  const [emailInput, setEmailInput] = useState('');
  const [phoneInput, setPhoneInput] = useState('');
  const [showMapUrl, setShowMapUrl] = useState(false);

  // Charger les champs UNE SEULE FOIS quand le modal s'ouvre
  useEffect(() => {
    if (show && !hasLoadedFields) {
      const loadFields = async () => {
        if (getDynamicFields && typeof getDynamicFields === 'function') {
          setLoadingFields(true);
          try {
            const loadedFields = await getDynamicFields();
            setDynamicFields(loadedFields);

            const initialFormData = {};
            loadedFields.forEach(field => {
              initialFormData[field.name] = initialData[field.name] !== undefined
                ? initialData[field.name]
                : (field.defaultValue !== undefined ? field.defaultValue : '');
            });
            setFormData(initialFormData);
          } catch (error) {
            console.error('Erreur chargement champs dynamiques:', error);
            setDynamicFields(fields);
            showNotification('Erreur', 'Impossible de charger les champs du formulaire', 'danger');
          } finally {
            setLoadingFields(false);
            setHasLoadedFields(true);
          }
        } else {
          setDynamicFields(fields);

          const initialFormData = {};
          fields.forEach(field => {
            initialFormData[field.name] = initialData[field.name] !== undefined
              ? initialData[field.name]
              : (field.defaultValue !== undefined ? field.defaultValue : '');
          });
          setFormData(initialFormData);
          setHasLoadedFields(true);
        }
      };

      loadFields();
    }
  }, [show]);

  // Réinitialiser quand le modal se ferme
  useEffect(() => {
    if (!show) {
      setFormData({});
      setErrors({});
      setSubmitError('');
      setSubmitSuccess(false);
      setHasLoadedFields(false);
      setDynamicFields(fields);
      setSearchResults({});
      setEmailInput('');
      setPhoneInput('');
      setShowMapUrl(false);
    }
  }, [show, fields]);

  // Empêcher le scroll du body pendant que le modal est ouvert
  useEffect(() => {
    if (show) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = original;
      };
    }
  }, [show]);

  const handleEmailArrayChange = useCallback((action, value = null) => {
    setFormData(prev => {
      let emails = prev.emails || [];
      if (Array.isArray(emails)) {
        emails = [...emails];
      } else if (typeof emails === 'string' && emails.trim()) {
        emails = emails.split(',').map(e => e.trim()).filter(e => e);
      } else {
        emails = [];
      }

      switch (action) {
        case 'add':
          if (value && !emails.includes(value)) emails.push(value);
          break;
        case 'remove':
          emails = emails.filter(email => email !== value);
          break;
        case 'set-primary':
          if (value) {
            setFormData(prevForm => ({ ...prevForm, primaryEmail: value }));
          }
          break;
        default:
          break;
      }

      return { ...prev, emails: emails.join(', ') };
    });
  }, []);

  const handlePhoneArrayChange = useCallback((action, value = null) => {
    setFormData(prev => {
      let telephones = prev.telephones || [];
      if (Array.isArray(telephones)) {
        telephones = [...telephones];
      } else if (typeof telephones === 'string' && telephones.trim()) {
        telephones = telephones.split(',').map(t => t.trim()).filter(t => t);
      } else {
        telephones = [];
      }

      switch (action) {
        case 'add':
          if (value && !telephones.includes(value)) telephones.push(value);
          break;
        case 'remove':
          telephones = telephones.filter(phone => phone !== value);
          break;
        case 'set-primary':
          if (value) {
            setFormData(prevForm => ({ ...prevForm, primaryTelephone: value }));
          }
          break;
        default:
          break;
      }

      return { ...prev, telephones: telephones.join(', ') };
    });
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    let fieldValue = value;

    if (type === 'checkbox') {
      fieldValue = checked;
    } else if (type === 'number') {
      fieldValue = value === '' ? '' : parseFloat(value);
    } else if (type === 'select-multiple') {
      fieldValue = Array.from(e.target.selectedOptions, option => option.value);
    }

    setFormData(prev => ({ ...prev, [name]: fieldValue }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  }, [errors]);

  const handleAddressSearch = useCallback((value, fieldName) => {
    if (!value || value.length < 2) {
      setSearchResults(prev => ({ ...prev, [fieldName]: [] }));
      return;
    }

    AutoComplete(value, (result) => {
      const predictions = result?.predictions?.map(p => ({
        id: p.place_id,
        label: p.description
      })) || [];
      setSearchResults(prev => ({ ...prev, [fieldName]: predictions }));
    });
  }, []);

  const handleSelectAddress = useCallback((item, fieldName) => {
    getPlaceDetails(item.id, (result) => {
      if (result?.result) {
        const placeDetails = result.result;
        const addressComponents = extractFromPlaceDetails(placeDetails);

        setFormData(prev => ({
          ...prev,
          [fieldName]: addressComponents.formatted || item.label,
          address_street: addressComponents.street,
          address_city: addressComponents.city,
          address_zip: addressComponents.zip,
          address_state: addressComponents.state,
          address_country: addressComponents.country,
          address_map_url: addressComponents.map_url,
          address_lat: addressComponents.lat,
          address_lng: addressComponents.lng
        }));

        if (addressComponents.map_url) setShowMapUrl(true);
      }
    });
    setSearchResults(prev => ({ ...prev, [fieldName]: [] }));
  }, []);

  const validateField = useCallback((fieldName, value) => {
    const fieldConfig = dynamicFields.find(f => f.name === fieldName);
    if (!fieldConfig) return '';

    if (fieldConfig.required && (value === undefined || value === null || value.toString().trim() === '')) {
      return 'Ce champ est requis';
    }

    if (fieldConfig.validate && typeof fieldConfig.validate === 'function') {
      const error = fieldConfig.validate(value, formData);
      if (error) return error;
    }

    return '';
  }, [dynamicFields, formData]);

  const validateForm = useCallback(() => {
    const newErrors = {};
    let isValid = true;

    dynamicFields.forEach(field => {
      const value = formData[field.name];
      const error = validateField(field.name, value);
      if (error) {
        newErrors[field.name] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  }, [dynamicFields, formData, validateField]);

  const handleModalHide = useCallback(() => {
    if (onHide) {
      onHide();
    } else if (onClose) {
      onClose(null);
    }
  }, [onHide, onClose]);

  const handleSubmit = useCallback(async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (loading || loadingFields || submitSuccess) return;

    if (!validateForm()) {
      showNotification('Validation', 'Veuillez corriger les erreurs dans le formulaire', 'warning');
      return;
    }

    setLoading(true);
    setSubmitError('');

    try {
      let result;

      if (submit && typeof submit === 'function') {
        result = await submit(formData);
      } else if (apiService && typeof apiService.create === 'function') {
        result = await apiService.create(formData);
      } else {
        throw new Error('Aucune méthode de soumission configurée');
      }

      setSubmitSuccess(true);
      showNotification('Succès', 'Élément créé avec succès', 'success');

      if (onSuccess && typeof onSuccess === 'function') {
        onSuccess(result);
      }

      if (refreshOnSuccess) {
        setTimeout(() => {
          if (onClose) {
            onClose(result);
          } else if (onHide) {
            onHide();
          }
        }, 1500);
      }
    } catch (error) {
      console.error('Erreur lors de la création:', error);

      if (error.response?.data?.errors) {
        const backendErrors = {};
        error.response.data.errors.forEach(err => {
          backendErrors[err.path] = err.msg;
        });
        setErrors(backendErrors);
        const errorMessage = error.response.data.message || 'Erreur de validation';
        setSubmitError(errorMessage);
        showNotification('Erreur', errorMessage, 'danger');
      } else {
        const errorMessage = error.response?.data?.message || error.message || 'Une erreur est survenue lors de la création';
        setSubmitError(errorMessage);
        showNotification('Erreur', errorMessage, 'danger');
      }
    } finally {
      setLoading(false);
    }
  }, [formData, validateForm, submit, apiService, onSuccess, refreshOnSuccess, onClose, onHide, loading, loadingFields, submitSuccess]);

  // ---- Emails array field --------------------------------------------------

  const renderEmailsArrayField = useCallback((field) => {
    const emails = formData.emails
      ? typeof formData.emails === 'string'
        ? formData.emails.split(',').map(e => e.trim()).filter(e => e)
        : formData.emails
      : [];

    const primaryEmail = formData.primaryEmail || '';
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailInput.trim());

    return (
      <div className="cm-form-group" key={field.name}>
        <label className="cm-label">
          {field.label}
          {field.required && <span className="cm-required">*</span>}
        </label>

        <div className="cm-input-group">
          <input
            type="email"
            className={`cm-input cm-input-group-input ${errors[field.name] ? 'cm-invalid' : ''}`}
            placeholder={field.placeholder || "Ajouter un email..."}
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                const email = emailInput.trim();
                if (email && isValidEmail) {
                  handleEmailArrayChange('add', email);
                  setEmailInput('');
                }
              }
            }}
          />
          <button
            type="button"
            className="cm-btn cm-btn-outline-primary cm-input-group-btn"
            onClick={() => {
              const email = emailInput.trim();
              if (email && isValidEmail) {
                handleEmailArrayChange('add', email);
                setEmailInput('');
              }
            }}
            disabled={!emailInput || !isValidEmail}
          >
            Ajouter
          </button>
        </div>
        <small className="cm-help-text">
          Appuyez sur Entrée ou cliquez sur Ajouter. Format requis: exemple@domaine.com
        </small>

        <div className="cm-list-mt">
          {emails.length > 0 ? (
            <div className="cm-list">
              {emails.map((email, index) => (
                <div key={index} className={`cm-list-item ${email === primaryEmail ? 'cm-list-item-primary' : ''}`}>
                  <div className="cm-list-item-main">
                    {email === primaryEmail && <span className="cm-badge-primary">Principal</span>}
                    <span className={email === primaryEmail ? 'cm-list-item-bold' : ''}>{email}</span>
                  </div>
                  <div className="cm-list-item-actions">
                    {email !== primaryEmail && (
                      <button
                        type="button"
                        className="cm-btn cm-btn-outline-primary cm-btn-sm"
                        onClick={() => handleEmailArrayChange('set-primary', email)}
                        title="Définir comme principal"
                      >
                        Définir comme principal
                      </button>
                    )}
                    <button
                      type="button"
                      className="cm-btn cm-btn-outline-danger cm-btn-sm"
                      onClick={() => handleEmailArrayChange('remove', email)}
                      title="Supprimer"
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="cm-empty-mini">
              <Icon icon="Envelope" size="sm" />
              <span className="ms-1">Aucun email ajouté</span>
            </div>
          )}
        </div>

        {field.helpText && <small className="cm-help-text">{field.helpText}</small>}
        {errors[field.name] && <div className="cm-error-text">{errors[field.name]}</div>}
      </div>
    );
  }, [formData, emailInput, errors, handleEmailArrayChange]);

  // ---- Phones array field --------------------------------------------------

  const renderPhonesArrayField = useCallback((field) => {
    const phones = formData.telephones
      ? typeof formData.telephones === 'string'
        ? formData.telephones.split(',').map(t => t.trim()).filter(t => t)
        : formData.telephones
      : [];

    const primaryPhone = formData.primaryTelephone || '';

    return (
      <div className="cm-form-group" key={field.name}>
        <label className="cm-label">
          {field.label}
          {field.required && <span className="cm-required">*</span>}
        </label>

        <div className="cm-input-group">
          <input
            type="tel"
            className={`cm-input cm-input-group-input ${errors[field.name] ? 'cm-invalid' : ''}`}
            placeholder={field.placeholder || "Ajouter un numéro de téléphone..."}
            value={phoneInput}
            onChange={(e) => setPhoneInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                const phone = phoneInput.trim();
                if (phone) {
                  handlePhoneArrayChange('add', phone);
                  setPhoneInput('');
                }
              }
            }}
          />
          <button
            type="button"
            className="cm-btn cm-btn-outline-primary cm-input-group-btn"
            onClick={() => {
              const phone = phoneInput.trim();
              if (phone) {
                handlePhoneArrayChange('add', phone);
                setPhoneInput('');
              }
            }}
            disabled={!phoneInput}
          >
            Ajouter
          </button>
        </div>
        <small className="cm-help-text">
          Appuyez sur Entrée ou cliquez sur Ajouter. Exemples: 01 23 45 67 89, +33 6 12 34 56 78
        </small>

        <div className="cm-list-mt">
          {phones.length > 0 ? (
            <div className="cm-list">
              {phones.map((phone, index) => (
                <div key={index} className={`cm-list-item ${phone === primaryPhone ? 'cm-list-item-primary' : ''}`}>
                  <div className="cm-list-item-main">
                    {phone === primaryPhone && <span className="cm-badge-primary">Principal</span>}
                    <span className={phone === primaryPhone ? 'cm-list-item-bold' : ''}>{phone}</span>
                  </div>
                  <div className="cm-list-item-actions">
                    {phone !== primaryPhone && (
                      <button
                        type="button"
                        className="cm-btn cm-btn-outline-primary cm-btn-sm"
                        onClick={() => handlePhoneArrayChange('set-primary', phone)}
                        title="Définir comme principal"
                      >
                        Définir comme principal
                      </button>
                    )}
                    <button
                      type="button"
                      className="cm-btn cm-btn-outline-danger cm-btn-sm"
                      onClick={() => handlePhoneArrayChange('remove', phone)}
                      title="Supprimer"
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="cm-empty-mini">
              <Icon icon="Phone" size="sm" />
              <span className="ms-1">Aucun téléphone ajouté</span>
            </div>
          )}
        </div>

        {field.helpText && <small className="cm-help-text">{field.helpText}</small>}
        {errors[field.name] && <div className="cm-error-text">{errors[field.name]}</div>}
      </div>
    );
  }, [formData, phoneInput, errors, handlePhoneArrayChange]);

  // ---- Google Maps preview --------------------------------------------------

  const renderMapUrlPreview = useCallback(() => {
    const mapUrl = formData.address_map_url;
    if (!mapUrl || !showMapUrl) return null;

    return (
      <div className="cm-form-group">
        <label className="cm-label">Lien Google Maps</label>
        <div className="cm-input-group">
          <input type="text" className="cm-input cm-input-group-input cm-input-readonly" value={mapUrl} readOnly />
          <a href={mapUrl} target="_blank" rel="noopener noreferrer" className="cm-btn cm-btn-primary cm-input-group-btn">
            <Icon icon="BoxArrowUpRight" size="sm" className="me-1" />
            Ouvrir
          </a>
        </div>
        <small className="cm-help-text">Lien généré automatiquement à partir de l'adresse</small>
      </div>
    );
  }, [formData.address_map_url, showMapUrl]);

  // ---- Generic field renderer --------------------------------------------------

  const renderField = useCallback((field) => {
    const {
      name,
      label,
      type = 'text',
      placeholder = '',
      options = [],
      rows = 3,
      disabled = false,
      helpText = '',
      condition,
      hidden = false,
      ...fieldProps
    } = field;

    if (condition && !condition(formData)) return null;
    if (hidden) return null;

    if (type === 'emails-array') return renderEmailsArrayField(field);
    if (type === 'phones-array') return renderPhonesArrayField(field);

    const value = formData[name] !== undefined ? formData[name] : '';
    const isDisabled = disabled || loading || loadingFields;
    const hasError = !!errors[name];

    if (type === 'autocomplete') {
      return (
        <div className="cm-form-group" key={name}>
          <label className="cm-label">
            {label}
            {field.required && <span className="cm-required">*</span>}
          </label>
          <SearchAutoComplete
            value={value}
            name={name}
            onChange={(e) => {
              handleChange(e);
              if (e.target.value.length >= 2) {
                handleAddressSearch(e.target.value, name);
              } else {
                setSearchResults(prev => ({ ...prev, [name]: [] }));
              }
            }}
            options={searchResults[name] || []}
            setSelectItem={(item) => handleSelectAddress(item, name)}
            label={placeholder}
            isTouched={!!formData[name]}
            isValid={!errors[name]}
            invalidFeedback={errors[name]}
          />
          {helpText && <small className="cm-help-text">{helpText}</small>}
        </div>
      );
    }

    if (type === 'checkbox') {
      return (
        <div className="cm-form-group" key={name}>
          <label className="cm-checkbox-row">
            <input
              type="checkbox"
              checked={!!value}
              onChange={handleChange}
              name={name}
              disabled={isDisabled}
              className="cm-checkbox"
              {...fieldProps}
            />
            {label}
          </label>
          {helpText && <small className="cm-help-text">{helpText}</small>}
          {hasError && <div className="cm-error-text">{errors[name]}</div>}
        </div>
      );
    }

    const renderControl = () => {
      switch (type) {
        case 'textarea':
          return (
            <textarea
              name={name}
              value={value}
              onChange={handleChange}
              disabled={isDisabled}
              rows={rows}
              className={`cm-textarea ${hasError ? 'cm-invalid' : ''}`}
              {...fieldProps}
            />
          );

        case 'select':
          return (
            <select
              name={name}
              value={value}
              onChange={handleChange}
              disabled={isDisabled}
              className={`cm-select ${hasError ? 'cm-invalid' : ''}`}
              {...fieldProps}
            >
              <option value="">{placeholder}</option>
              {options.map((option, index) => (
                <option key={index} value={option.value}>{option.label}</option>
              ))}
            </select>
          );

        case 'date': {
          const dateValue = value ? new Date(value).toISOString().split('T')[0] : '';
          return (
            <input
              type="date"
              name={name}
              value={dateValue}
              onChange={handleChange}
              disabled={isDisabled}
              className={`cm-input ${hasError ? 'cm-invalid' : ''}`}
              {...fieldProps}
            />
          );
        }

        default:
          return (
            <input
              type={type}
              name={name}
              value={value}
              onChange={handleChange}
              disabled={isDisabled}
              className={`cm-input ${hasError ? 'cm-invalid' : ''}`}
              {...fieldProps}
            />
          );
      }
    };

    return (
      <div className="cm-form-group" key={name}>
        <label className="cm-label">
          {label}
          {field.required && <span className="cm-required">*</span>}
        </label>
        {renderControl()}
        {helpText && <small className="cm-help-text">{helpText}</small>}
        {hasError && <div className="cm-error-text">{errors[name]}</div>}
      </div>
    );
  }, [formData, errors, loading, loadingFields, handleChange, searchResults, handleAddressSearch, handleSelectAddress, renderEmailsArrayField, renderPhonesArrayField]);

  if (!show) return null;

  const visibleFields = dynamicFields.filter(field => {
    if (field.hidden) return false;
    if (field.condition && !field.condition(formData)) return false;
    return true;
  });

  return (
    <div className="cm-backdrop" onMouseDown={(e) => e.stopPropagation()}>
      <div className={`cm-dialog cm-size-${size}`}>
        <div className="cm-content">
          <form
            onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); }}
            noValidate
          >
            <div className="cm-header">
              <h5 className="cm-title">{title}</h5>
              <button
                type="button"
                className="cm-close-btn"
                onClick={handleModalHide}
                aria-label="Fermer"
              >
                <Icon icon="Close" size="sm" color="dark" />
              </button>
            </div>

            <div className="cm-body">
              {submitSuccess ? (
                <div className="cm-success-state">
                  <div className="cm-success-icon">
                    <Icon icon="CheckCircle" size="2x" color="success" />
                  </div>
                  <h5 className="cm-success-title">Création réussie !</h5>
                  <p className="cm-success-sub">Fermeture automatique...</p>
                </div>
              ) : (
                <>
                  {loadingFields ? (
                    <div className="cm-loading-state">
                      <span className="cm-spinner" aria-hidden="true" />
                      <p className="cm-loading-text">Chargement...</p>
                    </div>
                  ) : dynamicFields.length === 0 ? (
                    <div className="cm-alert-warning">Aucun champ à afficher.</div>
                  ) : (
                    <div className="cm-row">
                      {visibleFields.map(field => (
                        <div key={field.name} className="cm-col" style={{ gridColumn: `span ${field.col || 12}` }}>
                          {renderField(field)}
                        </div>
                      ))}
                    </div>
                  )}
                  {submitError && <div className="cm-alert-danger">{submitError}</div>}
                </>
              )}
            </div>

            <div className="cm-footer">
              {!submitSuccess && (
                <>
                  <button
                    type="button"
                    className="cm-btn cm-btn-outline-secondary"
                    onClick={handleModalHide}   // <-- now uses the same close handler as X
                    disabled={loading || loadingFields}
                  >
                    {cancelLabel}
                  </button>
                  <button
                    type="button"
                    className="cm-btn cm-btn-primary"
                    onClick={handleSubmit}
                    disabled={loading || loadingFields || dynamicFields.length === 0}
                  >
                    {loading ? (
                      <>
                        <span className="cm-spinner cm-spinner-sm" aria-hidden="true" />
                        Création...
                      </>
                    ) : (
                      submitLabel
                    )}
                  </button>
                </>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

CreationModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  submit: PropTypes.func,
  title: PropTypes.string,
  fields: PropTypes.array,
  getDynamicFields: PropTypes.func,
  initialData: PropTypes.object,
  submitLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'lg', 'xl']),
  apiService: PropTypes.object,
  onSuccess: PropTypes.func,
  refreshOnSuccess: PropTypes.bool
};

export default CreationModal;